import type { Editor } from "@tiptap/react";

import React, { useRef, useState } from "react";

import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";

import { useReactRenderStore } from "@/features/react-editor/store/reactRenderStore";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { EditorContent } from "@tiptap/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUp,
  AtSign,
  ImagePlus,
  MousePointerClick,
  Square,
  X,
} from "lucide-react";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import { FilePreview } from "./FilePreview";
import { useAutosizeTextArea } from "./use-autosize-textarea";

import "./MessageInput/styles.css";

import { useIsChatLoading } from "@/features/react-editor/components/chat/store/chatStore";

interface MessageInputBaseProps {
  className?: string;
  disabled?: boolean;
  stop?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  enableInterrupt?: boolean;
  editor: Editor | null;
}

interface MessageInputProps extends MessageInputBaseProps {
  allowAttachments: true;
  files: File[] | null;
  setFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
}

export function MessageInput({
  className,
  stop,
  enableInterrupt = true,
  disabled,
  editor,
  ...props
}: MessageInputProps) {
  const isChatLoading = useIsChatLoading();
  const [isDragging, setIsDragging] = useState(false);
  const [showInterruptPrompt, setShowInterruptPrompt] = useState(false);
  const {
    displayMode,
    toggleDisplayMode,
    setContextMenuOpen,
    contextMenuOpen,
  } = useReactRenderStore();
  const isDebugMode = useIsDebugMode();

  useOnValueChange(isChatLoading, (currentValue, previousValue) => {
    if (previousValue && !currentValue) {
      setShowInterruptPrompt(false);
    }
  });

  const addFiles = (files: File[] | null) => {
    if (props.allowAttachments) {
      props.setFiles((currentFiles) => {
        if (currentFiles === null) {
          return files;
        }

        if (files === null) {
          return currentFiles;
        }

        return [...currentFiles, ...files];
      });
    }
  };

  const onDragOver = (event: React.DragEvent) => {
    if (props.allowAttachments !== true) {
      return;
    }
    event.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (event: React.DragEvent) => {
    if (props.allowAttachments !== true) {
      return;
    }
    event.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (event: React.DragEvent) => {
    setIsDragging(false);
    if (props.allowAttachments !== true) {
      return;
    }
    event.preventDefault();
    const dataTransfer = event.dataTransfer;
    if (dataTransfer.files.length) {
      addFiles(Array.from(dataTransfer.files));
    }
  };

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleAtSignClick = () => {
    setContextMenuOpen(!contextMenuOpen);
    // Focus the textarea after opening menu
    setTimeout(() => {
      textAreaRef.current?.focus();
    }, 0);
  };

  const showFileList =
    props.allowAttachments && props.files && props.files.length > 0;

  useAutosizeTextArea({
    ref: textAreaRef,
    maxHeight: 240,
    borderWidth: 1,
    dependencies: [showFileList],
  });

  return (
    <div
      className="relative w-full border border-border bg-sheet transition-[border] rounded-xl p-3 pr-3"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {enableInterrupt && (
        <InterruptPrompt
          isOpen={showInterruptPrompt}
          close={() => setShowInterruptPrompt(false)}
        />
      )}

      {props.allowAttachments && props.files && props.files.length > 0 && (
        <div className="z-20 overflow-x-auto py-2 pb-3">
          <div className="flex space-x-3">
            <AnimatePresence mode="popLayout">
              {props.files?.map((file) => {
                return (
                  <FilePreview
                    key={file.name + String(file.lastModified)}
                    file={file}
                    onRemove={() => {
                      props.setFiles((files) => {
                        if (!files) {
                          return null;
                        }

                        const filtered = Array.from(files).filter(
                          (f) => f !== file,
                        );
                        if (filtered.length === 0) {
                          return null;
                        }
                        return filtered;
                      });
                    }}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}

      {props.allowAttachments && <FileUploadOverlay isDragging={isDragging} />}
      <EditorContent className="pb-3" editor={editor} />

      <div className="z-20 flex gap-2 w-full">
        <div className="flex gap-2 mr-auto">
          <div className="flex h-8 w-8">
            <IconButton
              variant="secondary"
              icon={<AtSign className="h-4 w-4 text-muted" />}
              onClick={handleAtSignClick}
              tooltipText="Add reference"
            />
          </div>
          {props.allowAttachments && (
            <IconButton
              type="button"
              size="base"
              variant="secondary"
              aria-label="Attach an image"
              icon={<ImagePlus className="h-4 w-4" color="#475569" />}
              // eslint-disable-next-line
              onClick={async () => {
                const files = await showFileUploadDialog();
                addFiles(files);
              }}
            />
          )}
          {isDebugMode && (
            <Button
              type="button"
              size="base"
              variant="tertiary"
              layoutClassName={
                displayMode === "interactive"
                  ? "bg-surface px-3 py-2"
                  : "bg-menu-item-selected text-primary px-3 py-2"
              }
              startEnhancer={<MousePointerClick className="h-4 w-4" />}
              onClick={toggleDisplayMode}
            >
              Select
            </Button>
          )}
        </div>
        {isChatLoading && stop ? (
          <IconButton
            variant="secondary"
            type="button"
            size="base"
            aria-label="Stop generating"
            onClick={stop}
            icon={
              <Square className="h-3 w-3 animate-pulse" fill="currentColor" />
            }
          />
        ) : (
          <IconButton
            type="submit"
            size="base"
            variant="primary"
            aria-label="Send message"
            icon={<ArrowUp className="h-4 w-4" />}
          />
        )}
      </div>
    </div>
  );
}
MessageInput.displayName = "MessageInput";

interface InterruptPromptProps {
  isOpen: boolean;
  close: () => void;
}

function InterruptPrompt({ isOpen, close }: InterruptPromptProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ top: 0, filter: "blur(5px)" }}
          animate={{
            top: -40,
            filter: "blur(0px)",
            transition: {
              type: "spring",
              filter: { type: "tween" },
            },
          }}
          exit={{ top: 0, filter: "blur(5px)" }}
          className="absolute left-1/2 flex -translate-x-1/2 overflow-hidden whitespace-nowrap rounded-full border bg-background py-1 text-center text-sm text-muted-foreground"
        >
          <span className="ml-2.5">Press Enter again to interrupt</span>
          <button
            className="ml-1 mr-2.5 flex items-center"
            type="button"
            onClick={close}
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface FileUploadOverlayProps {
  isDragging: boolean;
}

function FileUploadOverlay({ isDragging }: FileUploadOverlayProps) {
  return (
    <AnimatePresence>
      {isDragging && (
        <motion.div
          className="pointer-events-none  flex items-center gap-3 p-3 rounded-lg border border-dashed border-border bg-white text-sm text-muted mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          aria-hidden
        >
          <img
            src="/images/chat/files-graphic.svg"
            alt="File upload graphic"
            width={52}
            height={52}
          />
          <div>
            <div className=" font-medium mb-1">
              Drop your files here to attach them.
            </div>
            <div>JPG, PNG, OR WEBP</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function showFileUploadDialog() {
  const input = document.createElement("input");

  input.type = "file";
  input.multiple = true;
  input.accept = "image/*";
  input.click();

  return new Promise<File[] | null>((resolve) => {
    input.addEventListener("change", (e) => {
      const files = (e.currentTarget as HTMLInputElement).files;

      if (files) {
        resolve(Array.from(files));
        return;
      }

      resolve(null);
    });
  });
}
